import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../../proto/index.js';
import { ErrorType, IMEvent, MessageSource, FlightStatus } from '../../core/consts.js';
import { Logger } from '../../core/metrics/logger.js';
import { BaseError } from '../../core/errors/BaseError.js';
import '../../core/metrics/monitor.js';
import '../../core/network/NetworkItem.js';
import { ContextClassBase, ServiceIdentity } from '../../core/service/RuntimeContext.js';
import { ConversationCoreInfo } from '../../core/model/ConversationCoreInfo.js';
import 'json-long';
import 'moize';
import '../../core/model/Message.js';
import 'js-md5';
import '../../core/utils/lz4.js';
import '../../core/utils/serializer.js';
import '../../adapter/manager/AdapterManager.js';
import '../../adapter/manager/types.js';
import '../../core/utils/ticker.js';
import '../../adapter/abstract/IMNetApi.js';
import '../../core/options.js';
import '../../core/utils/uuid.js';
import '../../core/service/ConversationManager.js';
import '../../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../../core/plugin/DbProxy.js';
import '../../core/metrics/trackerTypes.js';
import '../../core/model/CommandMessage.js';
import { BroadcastConversation } from '../model/BroadcastConversation.js';

class BroadcastManager extends ContextClassBase {
    constructor(ctx) {
        super(ctx);
        this.messages = new Map();
        this.conversation = new Map();
    }
    upsertOnline(local) {
        return __awaiter(this, void 0, void 0, function* () {
            const online = yield this.resolve(ServiceIdentity.ExtensionApi).GetConversationCoreInfo({
                conversationId: local.id,
                conversationShortId: Long.fromString(local.shortId),
                conversationType: local.type,
                inboxType: local.inboxType
            });
            const result = online.get_conversation_core_info_body;
            if (!result || !result.conversation_core_info) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.ConversationNotExist,
                    msg: `local conversation: ${local.id} not found online`,
                    sender: this,
                    args: {
                        conversationId: local.id
                    }
                });
            }
            const newConv = BroadcastConversation.fromServerConversation(this.ctx, result.conversation_core_info);
            this.conversation.set(local.id, newConv);
            return newConv;
        });
    }
    getWithOnline(conversationId, conversationShortId, conversationType) {
        return __awaiter(this, void 0, void 0, function* () {
            let conv = this.conversation.get(conversationId);
            if (!conv || conv.isOffline) {
                if (!conversationShortId || !conversationType) {
                    throw new BaseError({
                        ctx: this.ctx,
                        type: ErrorType.InvalidParam,
                        msg: 'no shortId and type provided',
                        sender: this
                    });
                }
                if (!conv) {
                    conv = new BroadcastConversation(this.ctx);
                    conv.id = conversationId;
                    conv.shortId = conversationShortId;
                    conv.type = im_proto.ConversationType.BROADCAST_CHAT;
                    conv.isOffline = true;
                    conv.coreInfo = new ConversationCoreInfo(conv);
                }
                conv = yield this.upsertOnline(conv);
            }
            return conv;
        });
    }
    upsert(msg) {
        const msgs = this.getConversationMsgs(msg.conversationId);
        const index = msgs.msgs.findIndex(item => item && (item.clientId === msg.clientId));
        let existMsg;
        if (index >= 0) {
            existMsg = msgs.msgs[index];
        }
        if (existMsg) {
            if (existMsg.type === msg.type) {
                msg = existMsg.merge(msg);
                msgs.msgs[index] = msg;
            }
            else {
                Logger.ctxWarn(this.ctx, 'try to merge different msg:', existMsg, msg);
                msgs.msgs[index] = msg; // 替换原来的消息
            }
        }
        else {
            this.insertMsg(msgs, msg);
        }
        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageUpsert, this, msg);
        // 忽略会话事件，和未读数
    }
    getList(conversationId) {
        const msgs = this.getConversationMsgs(conversationId);
        const newTail = msgs.tail;
        if (msgs.front < newTail) {
            return msgs.msgs.slice(msgs.front, newTail);
        }
        const front = msgs.msgs.slice(0, newTail);
        const tail = msgs.msgs.slice(msgs.front, BroadcastManager.MaxMsgSize);
        return tail.concat(front);
    }
    // insert msg to queue, and delete if neccesary
    insertMsg(msgs, msg) {
        const tail = msgs.tail;
        msgs.tail = (msgs.tail + 1) % BroadcastManager.MaxMsgSize; // 先把位置占住
        if (msgs.tail === msgs.front) {
            msgs.front = (msgs.front + 1) % BroadcastManager.MaxMsgSize;
        }
        msgs.msgs[tail] = msg;
    }
    get(conversationId, clientId) {
        const msg = this.getRaw(conversationId, clientId);
        if (!msg) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.MessageNotExist,
                msg: `message ${clientId} @ ${conversationId} not exist in local`,
                sender: this
            });
        }
        return msg;
    }
    getConversation(conversationId) {
        const conv = this.conversation.get(conversationId);
        if (!conv) {
            throw new BaseError({
                ctx: this.ctx,
                msg: `broadcast conversation ${conversationId} not exist in local`,
                type: ErrorType.ConversationNotExist,
                sender: this,
                args: {
                    conversationId
                }
            });
        }
        return conv;
    }
    getRaw(conversationId, clientId) {
        const msgs = this.getConversationMsgs(conversationId);
        const index = msgs.msgs.findIndex(item => item && (item.clientId === clientId));
        let existMsg;
        if (index >= 0) {
            existMsg = msgs.msgs[index];
        }
        return existMsg;
    }
    getByServerId(conversationId, serverId) {
        const msgs = this.getConversationMsgs(conversationId);
        const index = msgs.msgs.findIndex(item => item && (item.serverId === serverId));
        if (index < 0) {
            throw new BaseError({
                ctx: this.ctx,
                type: ErrorType.MessageNotExist,
                msg: `message ${serverId} @ ${conversationId} not exist in local`,
                sender: this
            });
        }
        return msgs.msgs[index];
    }
    // private msgQueueFull(msgs: BroadcastMsg) {
    //     const newTail = (msgs.tail + 1)%BroadcastManager.MaxMsgSize;
    //     if (newTail === msgs.front) {
    //         return true;
    //     }
    //     return false;
    // }
    // public markRecalled(conversationId: string, serverId: string) {
    //     const msg = this.getByServerId(conversationId, serverId);
    //     if (!msg.ext) {
    //         msg.ext = {};
    //     }
    //     msg.ext[InfoKeys.IsRecalled] = 'true';
    //     this.upsert(msg);
    //     const conv = this.conversation.get(conversationId);
    //     if (!conv) {
    //         return;
    //     }
    //     this.getService(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
    //     this.getService(ServiceIdentity.EventBus).emit(IMEvent.ConversationUpsert, this, conv);
    //     this.getService(ServiceIdentity.EventBus).emit(IMEvent.MessageUpsert, this, msg);
    //     this.getService(ServiceIdentity.EventBus).emit(IMEvent.MessageRecall, this, msg);
    // }
    getConversationMsgs(conId) {
        let arr = this.messages.get(conId);
        if (!arr) {
            arr = {
                msgs: new Array(BroadcastManager.MaxMsgSize),
                front: 0,
                tail: 0
            };
            this.messages.set(conId, arr);
        }
        return arr;
    }
    processNewMessagesFromPull(ms, source, logid) {
        return __awaiter(this, void 0, void 0, function* () {
            for (const m of ms) {
                if (!m.ext) {
                    m.ext = {};
                }
                yield this.processNewMessage(m, source);
            }
        });
    }
    processNewMessage(message, source) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!message.ext) {
                message.ext = {};
            }
            message.source = source;
            if (message.type >= 0) { // allow type === 0
                return this.processTextMessage(message);
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.UnknownMessageType,
                    msg: `unknown message type: ${message.type} for msg:${message.clientId}`,
                    sender: this
                });
            }
        });
    }
    dispose() {
        this.conversation.clear();
        this.messages.clear();
    }
    processTextMessage(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!msg.isNormalMsg) {
                return msg;
            }
            const msgOnline = msg.source !== undefined &&
                (msg.source === MessageSource.Online ||
                    msg.source === MessageSource.BroadcastLoadMore);
            const raw = this.getRaw(msg.conversationId, msg.clientId);
            const msgIsSelfSend = (raw === null || raw === void 0 ? void 0 : raw.flightStatus) !== undefined && msgOnline;
            this.upsert(msg);
            const updatedMsg = this.getRaw(msg.conversationId, msg.clientId);
            if (!raw) { // 收到新消息
                if (msgOnline) {
                    if (msg.sender !== this.ctx.option.userId) {
                        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ReceiveBroadcastNewMessage, this, msg);
                    }
                    else {
                        this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ReceiveBroadcastSelfMessage, this, msg);
                    }
                }
            }
            else {
                if (msgIsSelfSend && updatedMsg.flightStatus !== FlightStatus.Received) {
                    updatedMsg.flightStatus = FlightStatus.Received;
                    this.resolve(ServiceIdentity.EventBus).emit(IMEvent.ReceiveBroadcastSelfMessage, this, updatedMsg);
                    this.upsert(updatedMsg);
                }
            }
            return msg;
        });
    }
}
BroadcastManager.MaxMsgSize = 1001;

export { BroadcastManager };
//# sourceMappingURL=BroadcastManager.js.map
